import { useMessageSource } from "react-message-source";
import * as Yup from "yup";
import { useAddressValidator } from "../shared/components/address-select/validator";
import useBirthDateValidator from "../shared/components/birth-date/validator";
import useEmailValidator from "../shared/components/email/validator";
import { useTextInputValidator } from "../shared/components/formik/text-input/validator";
import useGenderValidator from "../shared/components/gender/validator";
import useLanguageValidator from "../shared/components/language/validator";
import usePhoneNumberValidator from "../shared/components/phone/validator";
import {
  useAmountValidator,
  useMandatoryStringTypeValidator,
  useYesNoValidator,
} from "../shared/components/validator";
import { CardLimitType } from "./cardholder-form/card-limit/types";
import useCardLimitOptionsValues from "./cardholder-form/card-limit/useCardLimitOptionsValues";
import useResidencePermitValues from "./cardholder-form/useResidencePermitValues";
import {
  CardApplicationDocument,
  Country,
} from "../../api/commercial/generated-types";
import { useIbanValidator } from "../../ui/widgets/iban/validator";

export default function useValidationSchema(limit: number) {
  const { getMessage, getMessageWithNamedParams } = useMessageSource();
  const yesNoValidator = useYesNoValidator();

  const spendingLimitValidator = Yup.number()
    .oneOf(
      useCardLimitOptionsValues().map((option) => option.value),
      getMessage("Validation.message-required"),
    )
    .max(
      limit,
      getMessageWithNamedParams(
        "Validation.message-ca-limit-exceeds-baa-limit",
        { limit },
      ),
    )
    .required(getMessage("Validation.message-required"));

  const residencePermitValidator = Yup.string()
    .nullable()
    .oneOf(
      useResidencePermitValues().map((option) => option.value),
      getMessage("Validation.message-required"),
    )
    .required(getMessage("Validation.message-required"));

  const decentralizedInvoicingSchema = Yup.object().shape({
    bankAccount: Yup.object().shape({
      bankName: useMandatoryStringTypeValidator(),
      iban: useIbanValidator(),
    }),
    fundsOwnership: useMandatoryStringTypeValidator(),
  });

  const amountValidator = useAmountValidator().max(
    limit,
    getMessageWithNamedParams("Validation.message-ca-limit-exceeds-baa-limit", {
      limit,
    }),
  );

  const cardholderValidationSchema = Yup.object().shape({
    gender: useGenderValidator(),
    firstName: useTextInputValidator(),
    lastName: useTextInputValidator(),
    fullStreet: useTextInputValidator(),
    address: useAddressValidator(),
    phoneBusiness: usePhoneNumberValidator(),
    mobile: usePhoneNumberValidator(),
    emailBusiness: useEmailValidator(),
    birthdate: useBirthDateValidator(),
    nationality: useMandatoryStringTypeValidator(),
    correspondenceLanguage: useLanguageValidator(),
    companyPosition: useTextInputValidator(),
    spendingLimit: spendingLimitValidator,
    spendingLimitOther: Yup.number()
      .nullable()
      .when("spendingLimit", {
        is: (spendingLimit: string) => {
          return parseInt(spendingLimit, 10) === CardLimitType.LIMIT_OTHER;
        },
        then: () => amountValidator,
      }),
    isCurrentPermanentEmployee: yesNoValidator,
    isCrossBorderWorker: Yup.string()
      .nullable()
      .when(["nationality", "address.country"], {
        is: (nationality: string, country: Country) => {
          return nationality !== "CH" && country.alpha2 !== "CH";
        },
        then: () => yesNoValidator,
      }),
    residencePermit: Yup.string()
      .nullable()
      .when(["nationality", "address.country"], {
        is: (nationality: string, country: Country) => {
          return nationality !== "CH" && country.alpha2 === "CH";
        },
        then: () => residencePermitValidator,
      }),
  });

  const fundsOwnershipCardholderValidator = Yup.object().shape({
    gender: useGenderValidator(),
    firstName: useTextInputValidator(),
    lastName: useTextInputValidator(),
    fullStreet: useTextInputValidator(),
    address: useAddressValidator(),
    birthdate: useBirthDateValidator(),
    nationality: useMandatoryStringTypeValidator(),
  });

  return Yup.object().shape({
    cardholder: Yup.object().when("isSameAsAdmin", {
      is: "no",
      then: () => cardholderValidationSchema,
    }),
    adminAsCardholder: Yup.object().when("isSameAsAdmin", {
      is: "yes",
      then: () => cardholderValidationSchema,
    }),
    decentralizedInvoicing: Yup.object().when("hasDecentralizedInvoicing", {
      is: true,
      then: () => decentralizedInvoicingSchema,
    }),
    fundsOwnershipCardholder: Yup.object().when(
      "decentralizedInvoicing.fundsOwnership",
      {
        is: "OTHER" as CardApplicationDocument["decentralizedInvoicing"]["fundsOwnership"],
        then: () => fundsOwnershipCardholderValidator,
      },
    ),
  });
}
